import React from 'react';
import { useTranslation } from 'react-i18next';
import useExcelStyles from '../useExcelStyles';
import useNoRowsTemplate from '../useNoRowsOverlay';
import useLoadingOverlay from '../useLoadingOverlay';
import { useDispatch } from '../../../redux/store';
import { openDialog, getConfirmCondition } from '../../../redux/slices/approval';
import dateTimeCellCallback from '../../../utils/ag-grid/processCellCallback/dateTimeCellCallback';
import steelMakerCellCallback from '../../../utils/ag-grid/processCellCallback/steelMakerCellCallback';
import businessTypeCellCallback from '../../../utils/ag-grid/processCellCallback/businessTypeCellCallback';
import steelCategoryCellCallback from '../../../utils/ag-grid/processCellCallback/steelCategoryCellCallback';
import conditionStatusCellCallback from '../../../utils/ag-grid/processCellCallback/conditionStatusCellCallback';
import { TOOLTIP_SHOW_DELAY } from '../../../constants';
import type { ApproveType } from '../../../types/approval';
import type { ProcessCellCallbackParams } from '../../../types/common';
import type { ColDef, ExcelStyle, GetContextMenuItemsParams, GridOptions, MenuItemDef } from 'ag-grid-community';

const useGridOptionsManageApproval = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { commonExcelStyles } = useExcelStyles();

  // Excelのスタイル設定
  // ----------------------------------------
  const excelStyles: ExcelStyle[] = [...commonExcelStyles];

  // Overlayの設定
  // ----------------------------------------
  const overlayLoadingTemplate: string = useLoadingOverlay();
  const overlayNoRowsTemplate: string = useNoRowsTemplate();

  // rowNodeIdの設定
  // ----------------------------------------
  const getRowNodeId = (params: ApproveType): string => {
    if (params.id !== undefined) {
      return String(params.id);
    } else if (params.organizationId) {
      return String(params.organizationId);
    } else {
      return params.updatedAt;
    }
  };

  // 右クリックメニューの設定
  // ----------------------------------------
  const getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    // console.log(params);
    if (params.node) {
      const data: ApproveType = params.node.data;
      dispatch(getConfirmCondition(data.id));
      // Excel用の変換を行う
      const processCellCallback = (params: ProcessCellCallbackParams): string => {
        // console.log(params.value);
        const myColDef: ColDef = params.column.getColDef();
        const myField: string | undefined = myColDef.field ?? myColDef.colId;
        if (myField === 'requestDate') {
          const formattedValue: string = dateTimeCellCallback(params);
          // console.log(formattedValue);
          return formattedValue;
        } else if (myField === 'steelMaker') {
          const steelMakerName: string = steelMakerCellCallback(params);
          // console.log(steelMakerName);
          return steelMakerName;
        } else if (myField === 'businessType') {
          const businessTypeName: string = businessTypeCellCallback(params);
          // console.log(businessTypeName);
          return businessTypeName;
        } else if (myField === 'steelCategory') {
          const steelCategoryName: string = steelCategoryCellCallback(params);
          // console.log(steelCategoryName);
          return steelCategoryName;
        } else if (myField === 'conditionStatus') {
          const formattedValue: string = conditionStatusCellCallback(params);
          // console.log(formattedValue);
          return formattedValue;
        } else {
          return params.value ?? '';
        }
      };
      // カスタムメニューの作成
      const editMenu = {
        name: t('approve'),
        action: () => {
          dispatch(openDialog());
        },
      };
      const exportMenu = {
        icon: '<span class="ag-icon ag-icon-save" unselectable="on"></span>',
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => params.api.exportDataAsCsv({ processCellCallback }),
          },
          {
            name: 'Excel Export (.xlsx)',
            action: () => params.api.exportDataAsExcel({ processCellCallback }),
          },
          // {
          //   name: 'Excel Export (.xml)',
          //   action: () => params.api.exportDataAsExcel({ exportMode: 'xml' }),
          // },
        ],
      };
      // 右クリックメニューの項目
      const menuItems = [editMenu, 'separator', exportMenu];
      return menuItems;
    } else {
      return [];
    }
  };

  // GridOptions全体
  // ----------------------------------------
  const gridOptions: GridOptions = {
    allowContextMenuWithControlKey: true,
    enableRangeSelection: true,
    excelStyles,
    getContextMenuItems,
    getRowNodeId,
    overlayLoadingTemplate,
    overlayNoRowsTemplate,
    tooltipShowDelay: TOOLTIP_SHOW_DELAY,
  };

  return { gridOptions };
};

export default useGridOptionsManageApproval;
